import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  FormHelperText,
  Chip,
  Divider,
  Alert,
  Tooltip,
  IconButton,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Collapse,
} from '@mui/material';
import {
  Cancel, Save, Add, Remove, Star, Delete, DeleteOutline, Business,
  ExpandMore, ExpandLess, ContentCopy, Check, Edit, Close,
} from '@mui/icons-material';
import { User, Company, ProductPlan } from 'modules/Interfaces/Company';
import { useSelector } from 'react-redux';
import { RootState } from 'state/store';
import { useCompanyData } from 'hooks/useCompanyData';
import { isSuperAdmin } from 'utils/isSuperAdmin';
import { apiClient } from 'utils/ApiClient';
import { Logger } from 'utils/Logger';

interface MembershipEntry {
  companyId: string;
  companyName: string;
  isPrimary: boolean;
  moduleAndRole: Array<{ module: string; role: string }>;
  joinedAt: string | null;
  status: string;
}

interface LocalMembership {
  companyId: string;
  moduleAndRole: Array<{ module: string; role: string }>;
}

interface UserDialogProps {
  open: boolean;
  onClose: () => void;
  user: Partial<User> | null;
  isEditing: boolean;
  onSave: (userData: Partial<User> & { password?: string; memberOfCompanies?: LocalMembership[] }) => Promise<void>;
  saveLoading: boolean;
  companies: Company[];
  onRefresh?: () => void;
}

type UserFormData = Partial<User> & { password?: string };

const ROLE_OPTIONS = ['user', 'admin'];

const ACTION_ICON_SX = { borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 } as const;

export const UserDialog: React.FC<UserDialogProps> = ({
  open, onClose, user, isEditing, onSave, saveLoading, companies, onRefresh,
}) => {
  const currentUser = useSelector((state: RootState) => state.authentication.user);
  const isUserSuperAdmin = currentUser?.email ? isSuperAdmin(currentUser.email) : false;
  const { company: currentCompany } = useCompanyData();

  // ─── Basic form state ───
  const [formData, setFormData] = useState<UserFormData>({
    email: '', mobile: '', status: 'ACTIVE', companyId: '', password: '',
    moduleAndRole: [],
    profile: {
      firstName: '', lastName: '', avatar: 'https://www.gravatar.com/avatar',
      addressLine1: '', addressLine2: '', city: '', state: '', country: '', zip: '',
      termsAndConditions: false, privacyPolicy: false, emailNotifications: false, smsNotifications: false, whatsAppNotifications: false,
      dob: new Date(),
    },
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [userLimitError, setUserLimitError] = useState('');

  // ─── Edit-mode membership state (API-backed) ───
  const [memberships, setMemberships] = useState<MembershipEntry[]>([]);
  const [membershipsLoading, setMembershipsLoading] = useState(false);
  const [membershipActionLoading, setMembershipActionLoading] = useState<string | null>(null);
  const [membershipError, setMembershipError] = useState('');
  const [expandedMembership, setExpandedMembership] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmRemoveCompanyId, setConfirmRemoveCompanyId] = useState<string | null>(null);
  const [confirmMakePrimaryCompanyId, setConfirmMakePrimaryCompanyId] = useState<string | null>(null);

  // Inline module editing for existing memberships
  const [editingModulesFor, setEditingModulesFor] = useState<string | null>(null);
  const [editModulesDraft, setEditModulesDraft] = useState<Array<{ module: string; role: string }>>([]);
  const [savingModules, setSavingModules] = useState(false);

  // Add membership form (edit mode)
  const [addMembershipOpen, setAddMembershipOpen] = useState(false);
  const [newMembershipCompanyId, setNewMembershipCompanyId] = useState('');
  const [newMembershipModules, setNewMembershipModules] = useState<Array<{ module: string; role: string }>>([{ module: '', role: 'user' }]);
  const [addMembershipLoading, setAddMembershipLoading] = useState(false);

  // ─── New user mode: additional memberships (local state) ───
  const [additionalMemberships, setAdditionalMemberships] = useState<LocalMembership[]>([]);

  // ─── Module seat info per company (companyId → seat data[]) ───
  const [companySeatsMap, setCompanySeatsMap] = useState<Record<string, Array<{ module: string; maxUsers: number; registeredUsers: number; remainingSeats: number }>>>({});
  const seatsBeingFetched = React.useRef<Set<string>>(new Set());

  // ──────────────────────────────────────────
  //  Fetch module seat info
  // ──────────────────────────────────────────

  const fetchCompanySeats = useCallback(async (companyId: string) => {
    if (!companyId || seatsBeingFetched.current.has(companyId)) return;
    seatsBeingFetched.current.add(companyId);
    try {
      const result = await apiClient.get(`/user/company/${companyId}/module-seats`);
      const data = result.data || result;
      setCompanySeatsMap(prev => ({ ...prev, [companyId]: Array.isArray(data) ? data : [] }));
    } catch (err) {
      Logger.error('Failed to fetch module seats:', err);
    } finally {
      seatsBeingFetched.current.delete(companyId);
    }
  }, []);

  // ──────────────────────────────────────────
  //  Helpers
  // ──────────────────────────────────────────

  const getCompanyName = (companyId: string): string => {
    if (!companyId) return 'No Company';
    return companies.find(c => c._id === companyId)?.name || 'Unknown Company';
  };

  const getAvailableModulesForCompany = (companyId: string): ProductPlan[] => {
    const company = companies.find(c => c._id === companyId);
    if (!company?.productPlans) return [];
    const now = new Date();
    return company.productPlans.filter(plan => {
      const start = new Date(plan.startedAt);
      const end = new Date(plan.endsAt);
      return plan.status === 'active' && now >= start && now <= end;
    });
  };

  const availableModules = useMemo(() => {
    const cId = formData.companyId || currentCompany?._id || '';
    return getAvailableModulesForCompany(cId);
  }, [formData.companyId, companies, currentCompany]);

  const companiesNotMemberOf = useMemo(() => {
    if (isEditing) {
      const memberIds = new Set(memberships.map(m => m.companyId));
      return companies.filter(c => !memberIds.has(c._id));
    }
    const usedIds = new Set([formData.companyId, ...additionalMemberships.map(m => m.companyId)].filter(Boolean));
    return companies.filter(c => !usedIds.has(c._id));
  }, [isEditing, memberships, companies, formData.companyId, additionalMemberships]);

  const isAllowedEmailDomain = (email: string): boolean => {
    const allowedDomains = ['campaignking.ai', 'sharadtech.com', 'scenicmediahub.com'];
    const d = email.split('@')[1]?.toLowerCase();
    return d ? allowedDomains.includes(d) : false;
  };

  // ──────────────────────────────────────────
  //  Edit-mode: fetch memberships
  // ──────────────────────────────────────────

  const fetchMemberships = useCallback(async (userId: string) => {
    setMembershipsLoading(true);
    setMembershipError('');
    try {
      const result = await apiClient.get(`/user/${userId}/memberships`);
      const data = result.data || result;
      setMemberships(Array.isArray(data) ? data : []);
    } catch (err) {
      Logger.error('Failed to fetch memberships:', err);
      setMembershipError('Failed to load company memberships');
    } finally {
      setMembershipsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && isEditing && user?._id) {
      fetchMemberships(user._id);
    } else {
      setMemberships([]);
      setMembershipError('');
      setExpandedMembership(null);
      setConfirmRemoveCompanyId(null);
      setConfirmMakePrimaryCompanyId(null);
      setEditingModulesFor(null);
      setAddMembershipOpen(false);
      setAdditionalMemberships([]);
    }
    if (!open) {
      setCompanySeatsMap({});
      seatsBeingFetched.current.clear();
    }
  }, [open, isEditing, user?._id, fetchMemberships]);

  // Fetch seats for all membership companies (edit mode)
  useEffect(() => {
    if (open && isEditing && memberships.length > 0) {
      memberships.forEach(m => { if (m.companyId) fetchCompanySeats(m.companyId); });
    }
  }, [open, isEditing, memberships, fetchCompanySeats]);

  // Fetch seats for primary company (new user mode)
  useEffect(() => {
    if (open && !isEditing && formData.companyId) {
      fetchCompanySeats(formData.companyId);
    }
  }, [open, isEditing, formData.companyId, fetchCompanySeats]);

  // Fetch seats for additional membership companies (new user mode)
  useEffect(() => {
    if (open && !isEditing) {
      additionalMemberships.forEach(am => { if (am.companyId) fetchCompanySeats(am.companyId); });
    }
  }, [open, isEditing, additionalMemberships, fetchCompanySeats]);

  // Fetch seats for new membership company being added (edit mode)
  useEffect(() => {
    if (open && isEditing && newMembershipCompanyId) {
      fetchCompanySeats(newMembershipCompanyId);
    }
  }, [open, isEditing, newMembershipCompanyId, fetchCompanySeats]);

  // ──────────────────────────────────────────
  //  Edit-mode: membership actions
  // ──────────────────────────────────────────

  const handleRemoveMembership = async (companyId: string) => {
    if (!user?._id) return;
    setMembershipActionLoading(companyId);
    setMembershipError('');
    try {
      await apiClient.delete(`/user/${user._id}/membership/${companyId}`);
      setConfirmRemoveCompanyId(null);
      await fetchMemberships(user._id);
      fetchCompanySeats(companyId);
      onRefresh?.();
    } catch (err: any) {
      setMembershipError(err?.message || 'Failed to remove membership');
    } finally {
      setMembershipActionLoading(null);
    }
  };

  const handleMakePrimary = async (companyId: string) => {
    if (!user?._id) return;
    setMembershipActionLoading(companyId);
    setMembershipError('');
    try {
      await apiClient.patch(`/user/${user._id}/primary-company`, { newPrimaryCompanyId: companyId });
      setConfirmMakePrimaryCompanyId(null);
      await fetchMemberships(user._id);
      onRefresh?.();
    } catch (err: any) {
      setMembershipError(err?.message || 'Failed to change primary company');
    } finally {
      setMembershipActionLoading(null);
    }
  };

  const handleCopyCompanyId = (companyId: string) => {
    navigator.clipboard.writeText(companyId);
    setCopiedId(companyId);
    setTimeout(() => setCopiedId(null), 1500);
  };

  // ─── Inline module editing (edit mode) ───

  const startEditModules = (m: MembershipEntry) => {
    setEditingModulesFor(m.companyId);
    setEditModulesDraft(m.moduleAndRole.map(mr => ({ ...mr })));
    setExpandedMembership(m.companyId);
  };

  const cancelEditModules = () => {
    setEditingModulesFor(null);
    setEditModulesDraft([]);
  };

  const saveEditModules = async () => {
    if (!user?._id || !editingModulesFor) return;
    const valid = editModulesDraft.every(mr => mr.module && mr.role);
    if (!valid || editModulesDraft.length === 0) {
      setMembershipError('Each module entry must have a module and role selected');
      return;
    }
    setSavingModules(true);
    setMembershipError('');
    try {
      const companyId = editingModulesFor;
      await apiClient.patch(`/user/${user._id}/membership/${companyId}/modules`, { moduleAndRole: editModulesDraft });
      setEditingModulesFor(null);
      await fetchMemberships(user._id);
      fetchCompanySeats(companyId);
      onRefresh?.();
    } catch (err: any) {
      setMembershipError(err?.message || 'Failed to update modules');
    } finally {
      setSavingModules(false);
    }
  };

  // ─── Add membership (edit mode) ───

  const handleAddMembership = async () => {
    if (!user?._id) return;
    const valid = newMembershipCompanyId && newMembershipModules.every(mr => mr.module && mr.role);
    if (!valid || newMembershipModules.length === 0) {
      setMembershipError('Select a company and fill all module/role entries');
      return;
    }
    setAddMembershipLoading(true);
    setMembershipError('');
    try {
      const cId = newMembershipCompanyId;
      await apiClient.post(`/user/${user._id}/membership`, {
        companyId: cId,
        moduleAndRole: newMembershipModules,
      });
      setAddMembershipOpen(false);
      setNewMembershipCompanyId('');
      setNewMembershipModules([{ module: '', role: 'user' }]);
      await fetchMemberships(user._id);
      fetchCompanySeats(cId);
      onRefresh?.();
    } catch (err: any) {
      setMembershipError(err?.message || 'Failed to add membership');
    } finally {
      setAddMembershipLoading(false);
    }
  };

  // ──────────────────────────────────────────
  //  Form data init
  // ──────────────────────────────────────────

  useEffect(() => {
    if (companies.length === 0 && isEditing) return;
    if (user) {
      setFormData({
        ...user,
        status: user.status?.toUpperCase() || 'ACTIVE',
        password: '',
        moduleAndRole: user.moduleAndRole || [],
        profile: {
          firstName: '', lastName: '', avatar: 'https://www.gravatar.com/avatar',
          addressLine1: '', addressLine2: '', city: '', state: '', country: '', zip: '',
          termsAndConditions: false, privacyPolicy: false, emailNotifications: false, smsNotifications: false, whatsAppNotifications: false,
          ...user.profile,
          dob: user.profile?.dob ? new Date(user.profile.dob) : new Date(),
        },
      });
    } else if (!isEditing) {
      setFormData(prev => ({
        ...prev,
        companyId: isUserSuperAdmin ? '' : (currentUser?.companyId || ''),
        moduleAndRole: [],
      }));
      setAdditionalMemberships([]);
    }
  }, [user, isEditing, isUserSuperAdmin, currentUser?.companyId, companies]);

  // ──────────────────────────────────────────
  //  Form handlers
  // ──────────────────────────────────────────

  const handleInputChange = (field: string, value: any) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: { ...(prev[parent as keyof UserFormData] as any), [child]: value },
      }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
    if (field === 'companyId') setUserLimitError('');
  };

  const handleModuleRoleChange = (index: number, field: 'module' | 'role', value: string) => {
    setFormData(prev => {
      const updated = [...(prev.moduleAndRole || [])];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, moduleAndRole: updated };
    });
    if (errors[`moduleAndRole.${index}.${field}`]) {
      setErrors(prev => ({ ...prev, [`moduleAndRole.${index}.${field}`]: '' }));
    }
  };

  const addModuleRole = () => {
    setFormData(prev => ({
      ...prev,
      moduleAndRole: [...(prev.moduleAndRole || []), { module: '', role: 'user' }],
    }));
  };

  const removeModuleRole = (index: number) => {
    setFormData(prev => ({
      ...prev,
      moduleAndRole: (prev.moduleAndRole || []).filter((_, i) => i !== index),
    }));
  };

  // ─── Additional memberships (new user mode) ───

  const addAdditionalMembership = () => {
    setAdditionalMemberships(prev => [...prev, { companyId: '', moduleAndRole: [{ module: '', role: 'user' }] }]);
  };

  const removeAdditionalMembership = (index: number) => {
    setAdditionalMemberships(prev => prev.filter((_, i) => i !== index));
  };

  const updateAdditionalMembershipCompany = (index: number, companyId: string) => {
    setAdditionalMemberships(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], companyId };
      return updated;
    });
  };

  const addModuleToAdditional = (membershipIndex: number) => {
    setAdditionalMemberships(prev => {
      const updated = [...prev];
      updated[membershipIndex] = {
        ...updated[membershipIndex],
        moduleAndRole: [...updated[membershipIndex].moduleAndRole, { module: '', role: 'user' }],
      };
      return updated;
    });
  };

  const removeModuleFromAdditional = (membershipIndex: number, moduleIndex: number) => {
    setAdditionalMemberships(prev => {
      const updated = [...prev];
      updated[membershipIndex] = {
        ...updated[membershipIndex],
        moduleAndRole: updated[membershipIndex].moduleAndRole.filter((_, i) => i !== moduleIndex),
      };
      return updated;
    });
  };

  const updateAdditionalModule = (membershipIndex: number, moduleIndex: number, field: 'module' | 'role', value: string) => {
    setAdditionalMemberships(prev => {
      const updated = [...prev];
      const mods = [...updated[membershipIndex].moduleAndRole];
      mods[moduleIndex] = { ...mods[moduleIndex], [field]: value };
      updated[membershipIndex] = { ...updated[membershipIndex], moduleAndRole: mods };
      return updated;
    });
  };

  // ──────────────────────────────────────────
  //  Validation & Save
  // ──────────────────────────────────────────

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!formData.email?.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
    if (!isEditing && !formData.password) newErrors.password = 'Password is required';
    if (!formData.mobile?.trim()) newErrors.mobile = 'Mobile is required';
    if (!formData.profile?.firstName?.trim()) newErrors['profile.firstName'] = 'First name is required';
    if (!formData.profile?.lastName?.trim()) newErrors['profile.lastName'] = 'Last name is required';
    if (!formData.profile?.country?.trim()) newErrors['profile.country'] = 'Country is required';
    if (!formData.profile?.dob) newErrors['profile.dob'] = 'Date of birth is required';
    if (!formData.status?.trim()) newErrors.status = 'Status is required';
    if (!isEditing && !formData.companyId?.trim()) newErrors.companyId = 'Company is required';
    if (!isEditing) {
      if (!formData.moduleAndRole || formData.moduleAndRole.length === 0) {
        newErrors.moduleAndRole = 'At least one module and role is required for the primary company';
      } else {
        formData.moduleAndRole.forEach((mr, i) => {
          if (!mr.module?.trim()) newErrors[`moduleAndRole.${i}.module`] = 'Module is required';
          if (!mr.role?.trim()) newErrors[`moduleAndRole.${i}.role`] = 'Role is required';
        });
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = (): boolean => {
    const basicOk = !!(
      formData.email?.trim() && formData.mobile?.trim() &&
      formData.profile?.firstName?.trim() && formData.profile?.lastName?.trim() &&
      formData.profile?.country?.trim() && formData.profile?.dob && formData.status?.trim() &&
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email || '')
    );
    if (!isEditing) {
      if (!formData.password) return false;
      if (!formData.companyId?.trim()) return false;
      if (!formData.moduleAndRole || formData.moduleAndRole.length === 0) return false;
      if (formData.moduleAndRole.some(mr => !mr.module || !mr.role)) return false;
      for (const am of additionalMemberships) {
        if (!am.companyId) return false;
        if (am.moduleAndRole.length === 0 || am.moduleAndRole.some(mr => !mr.module || !mr.role)) return false;
      }
    }
    return basicOk;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    try {
      setUserLimitError('');
      const userDataToSave: any = {
        ...formData,
        status: formData.status ? formData.status.toLowerCase() : 'active',
        companyId: isEditing ? formData.companyId : (isUserSuperAdmin ? formData.companyId : (currentUser?.companyId || '')),
      };
      if (isEditing) {
        // In edit mode, moduleAndRole and memberOfCompanies are managed via
        // dedicated API calls (Save Modules, Add/Remove Membership) so strip
        // them to avoid overwriting changes made through those APIs.
        delete userDataToSave.moduleAndRole;
        delete userDataToSave.memberOfCompanies;
      } else {
        userDataToSave.moduleAndRole = formData.moduleAndRole || [];
        if (additionalMemberships.length > 0) {
          userDataToSave.memberOfCompanies = additionalMemberships;
        }
      }
      await onSave(userDataToSave);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : 'An unknown error occurred';
      if (msg.toLowerCase().includes('limit exceeded')) setUserLimitError(msg);
      else { setErrorMessage(msg); setErrorDialogOpen(true); }
    }
  };

  const formatDateForInput = (date: Date | string | undefined): string => {
    if (!date) return '';
    try {
      const d = date instanceof Date ? date : new Date(date);
      return isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0];
    } catch { return ''; }
  };

  // ──────────────────────────────────────────
  //  Render helpers: module rows
  // ──────────────────────────────────────────

  const renderModuleRows = (
    modules: Array<{ module: string; role: string }>,
    companyId: string,
    onChange: (idx: number, field: 'module' | 'role', val: string) => void,
    onRemove: (idx: number) => void,
    onAdd: () => void,
    disabled = false,
    minOne = true,
    existingUserModules?: string[],
  ) => {
    const plans = getAvailableModulesForCompany(companyId);
    const seats = companySeatsMap[companyId] || [];
    const seatsMap = new Map(seats.map(s => [s.module, s]));
    const selectedModulesInList = new Set(modules.map(mr => mr.module).filter(Boolean));
    const allModulesAtCapacity = plans.length > 0 && plans.every(p => {
      const seatInfo = seatsMap.get(p.module);
      const alreadyHas = existingUserModules?.includes(p.module);
      if (alreadyHas) return false;
      return seatInfo ? seatInfo.remainingSeats <= 0 : false;
    });
    const allModulesSelected = plans.length > 0 && plans.every(p => selectedModulesInList.has(p.module));

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {modules.map((mr, idx) => {
          const otherSelected = new Set(modules.filter((_, i) => i !== idx).map(m => m.module).filter(Boolean));
          return (
            <Box key={idx} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <FormControl size="small" sx={{ flex: 1 }} disabled={disabled}>
                <InputLabel>Module</InputLabel>
                <Select value={mr.module || ''} label="Module" onChange={(e) => onChange(idx, 'module', e.target.value as string)}>
                  {plans.map(p => {
                    const seatInfo = seatsMap.get(p.module);
                    const alreadyHas = existingUserModules?.includes(p.module);
                    const noSeats = seatInfo ? seatInfo.remainingSeats <= 0 && !alreadyHas : false;
                    const dupSelected = otherSelected.has(p.module);
                    const isDisabled = noSeats || dupSelected;
                    const seatLabel = seatInfo
                      ? ` — ${seatInfo.remainingSeats} seat${seatInfo.remainingSeats !== 1 ? 's' : ''} left`
                      : '';
                    return (
                      <MenuItem key={p.module} value={p.module} disabled={isDisabled}>
                        {p.module} ({p.licenseType}){seatLabel}
                        {dupSelected && ' (already added)'}
                      </MenuItem>
                    );
                  })}
                  {mr.module && !plans.find(p => p.module === mr.module) && (
                    <MenuItem value={mr.module}>{mr.module} (current)</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 110 }} disabled={disabled}>
                <InputLabel>Role</InputLabel>
                <Select value={mr.role || ''} label="Role" onChange={(e) => onChange(idx, 'role', e.target.value as string)}>
                  {ROLE_OPTIONS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                </Select>
              </FormControl>
              <Tooltip title={minOne && modules.length <= 1 ? 'At least one module required' : 'Remove'}>
                <span>
                  <Button color="error" variant="contained" size="small"
                    onClick={() => onRemove(idx)}
                    disabled={disabled || (minOne && modules.length <= 1)}
                    sx={ACTION_ICON_SX}>
                    <Delete fontSize="small" />
                  </Button>
                </span>
              </Tooltip>
            </Box>
          );
        })}
        <Tooltip title={allModulesSelected ? 'All available modules already added' : (allModulesAtCapacity ? 'No modules with available seats' : 'Add Module')}>
          <span>
            <Button color="secondary" variant="contained" size="small"
              onClick={onAdd} disabled={disabled || allModulesSelected || allModulesAtCapacity} sx={ACTION_ICON_SX}>
              <Add fontSize="small" />
            </Button>
          </span>
        </Tooltip>
      </Box>
    );
  };

  // ──────────────────────────────────────────
  //  Render: Membership card (edit mode)
  // ──────────────────────────────────────────

  const renderMembershipCard = (m: MembershipEntry) => {
    const isExpanded = expandedMembership === m.companyId;
    const isActionLoading = membershipActionLoading === m.companyId;
    const isEditingModules = editingModulesFor === m.companyId;

    return (
      <Card key={m.companyId} variant="outlined" sx={{
        borderLeft: m.isPrimary ? '4px solid' : '1px solid',
        borderLeftColor: m.isPrimary ? 'warning.main' : 'divider',
      }}>
        <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
          {/* Header row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Business fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: m.isPrimary ? 700 : 400 }}>
              {m.companyName}
            </Typography>
            {m.isPrimary && <Chip label="Primary" size="small" color="warning" variant="outlined" sx={{ fontWeight: 700, height: 22 }} />}
            <Chip label={m.status?.toUpperCase() || 'ACTIVE'} size="small"
              color={m.status?.toLowerCase() === 'active' ? 'success' : 'default'}
              sx={{ height: 22, fontSize: '0.7rem' }} />
            <Tooltip title={copiedId === m.companyId ? 'Copied!' : 'Copy Company ID'}>
              <IconButton size="small" onClick={() => handleCopyCompanyId(m.companyId)}>
                {copiedId === m.companyId ? <Check fontSize="small" color="success" /> : <ContentCopy fontSize="small" />}
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setExpandedMembership(isExpanded ? null : m.companyId)}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Box>

          {/* Collapsed chip summary */}
          {!isExpanded && m.moduleAndRole.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
              {m.moduleAndRole.map((mr, idx) => (
                <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
                  color={mr.role === 'admin' ? 'warning' : 'default'}
                  sx={(theme) => ({
                    fontWeight: 'bold',
                    ...(mr.role !== 'admin' && {
                      backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                      color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                    }),
                  })} />
              ))}
            </Box>
          )}

          {/* Expanded body */}
          <Collapse in={isExpanded}>
            <Box sx={{ mt: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Company ID: {m.companyId}
              </Typography>
              {m.joinedAt && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Joined: {new Date(m.joinedAt).toLocaleDateString()}
                </Typography>
              )}

              {/* Modules section */}
              <Box sx={(theme) => ({
                border: `1px solid ${theme.palette.secondary.main}`,
                borderRadius: 1,
                p: 1.5,
                mb: 1,
              })}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="subtitle2" color="secondary" sx={{ fontWeight: 700, flex: 1 }}>
                    Modules & Roles
                  </Typography>
                  {!isEditingModules && (
                    <Tooltip title="Edit modules & roles">
                      <Button color="secondary" variant="contained" size="small"
                        onClick={() => startEditModules(m)} sx={ACTION_ICON_SX}>
                        <Edit fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                </Box>

                {isEditingModules ? (
                  <Box>
                    {renderModuleRows(
                      editModulesDraft,
                      m.companyId,
                      (idx, field, val) => {
                        setEditModulesDraft(prev => {
                          const u = [...prev]; u[idx] = { ...u[idx], [field]: val }; return u;
                        });
                      },
                      (idx) => setEditModulesDraft(prev => prev.filter((_, i) => i !== idx)),
                      () => setEditModulesDraft(prev => [...prev, { module: '', role: 'user' }]),
                      savingModules,
                      true,
                      m.moduleAndRole.map(mr => mr.module),
                    )}
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
                      <Button size="small" variant="outlined" color="secondary" startIcon={<Close />}
                        disabled={savingModules} onClick={cancelEditModules}>Cancel</Button>
                      <Button size="small" variant="contained" color="success" startIcon={<Save />}
                        disabled={savingModules} onClick={saveEditModules}>
                        {savingModules ? 'Saving...' : 'Save'}
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    {m.moduleAndRole.length > 0 ? m.moduleAndRole.map((mr, idx) => (
                      <Chip key={idx} label={`${mr.module}: ${mr.role}`} size="small"
                        color={mr.role === 'admin' ? 'warning' : 'default'}
                        sx={(theme) => ({
                          fontWeight: 'bold',
                          ...(mr.role !== 'admin' && {
                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300],
                            color: theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black,
                          }),
                        })} />
                    )) : (
                      <Typography variant="caption" color="text.secondary">No modules assigned</Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Confirm dialogs */}
              {confirmRemoveCompanyId === m.companyId && !m.isPrimary && (
                <Alert severity="warning" sx={{ mt: 1 }} action={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" color="error" variant="contained" disabled={isActionLoading}
                      onClick={() => handleRemoveMembership(m.companyId)}>
                      {isActionLoading ? <CircularProgress size={16} /> : 'Yes, Remove'}
                    </Button>
                    <Button size="small" onClick={() => setConfirmRemoveCompanyId(null)}>Cancel</Button>
                  </Box>
                }>Remove membership from {m.companyName}?</Alert>
              )}
              {confirmMakePrimaryCompanyId === m.companyId && !m.isPrimary && (
                <Alert severity="info" sx={{ mt: 1 }} action={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <Button size="small" color="primary" variant="contained" disabled={isActionLoading}
                      onClick={() => handleMakePrimary(m.companyId)}>
                      {isActionLoading ? <CircularProgress size={16} /> : 'Yes, Change'}
                    </Button>
                    <Button size="small" onClick={() => setConfirmMakePrimaryCompanyId(null)}>Cancel</Button>
                  </Box>
                }>Make {m.companyName} the primary company?</Alert>
              )}
            </Box>
          </Collapse>
        </CardContent>

        {isExpanded && !m.isPrimary && !isEditingModules && (
          <CardActions sx={{ pt: 0, px: 2, pb: 1, justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" variant="outlined" startIcon={<Star fontSize="small" />} color="warning"
              disabled={isActionLoading} onClick={() => setConfirmMakePrimaryCompanyId(m.companyId)}>
              Make Primary
            </Button>
            <Tooltip title="Remove membership">
              <span>
                <Button color="error" variant="contained" size="small"
                  disabled={isActionLoading} onClick={() => setConfirmRemoveCompanyId(m.companyId)}
                  sx={ACTION_ICON_SX}>
                  <Delete fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          </CardActions>
        )}
      </Card>
    );
  };

  // ──────────────────────────────────────────
  //  Render: Add membership form (edit mode)
  // ──────────────────────────────────────────

  const renderAddMembershipForm = () => (
    <Card variant="outlined" sx={{ borderStyle: 'dashed', mt: 1 }}>
      <CardContent sx={{ py: 1.5, px: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Company Membership</Typography>
        <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
          <InputLabel>Company</InputLabel>
          <Select value={newMembershipCompanyId} label="Company"
            onChange={(e) => setNewMembershipCompanyId(e.target.value as string)}>
            {companiesNotMemberOf.map(c => (
              <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {newMembershipCompanyId && renderModuleRows(
          newMembershipModules,
          newMembershipCompanyId,
          (idx, field, val) => {
            setNewMembershipModules(prev => {
              const u = [...prev]; u[idx] = { ...u[idx], [field]: val }; return u;
            });
          },
          (idx) => setNewMembershipModules(prev => prev.filter((_, i) => i !== idx)),
          () => setNewMembershipModules(prev => [...prev, { module: '', role: 'user' }]),
          addMembershipLoading,
          true,
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, justifyContent: 'flex-end' }}>
          <Button size="small" variant="outlined" color="secondary" startIcon={<Close />} onClick={() => {
            setAddMembershipOpen(false);
            setNewMembershipCompanyId('');
            setNewMembershipModules([{ module: '', role: 'user' }]);
          }}>Cancel</Button>
          <Button size="small" variant="contained" color="success" startIcon={<Add />}
            disabled={addMembershipLoading || !newMembershipCompanyId}
            onClick={handleAddMembership}>
            {addMembershipLoading ? 'Adding...' : 'Add'}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  // ──────────────────────────────────────────
  //  Render: Additional memberships (new user)
  // ──────────────────────────────────────────

  const renderAdditionalMembership = (am: LocalMembership, index: number) => {
    const plans = am.companyId ? getAvailableModulesForCompany(am.companyId) : [];
    return (
      <Card key={index} variant="outlined" sx={{ mt: 1 }}>
        <CardContent sx={{ py: 1.5, px: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="subtitle2">Additional Company {index + 1}</Typography>
            <Tooltip title="Remove company membership">
              <Button color="error" variant="contained" size="small"
                onClick={() => removeAdditionalMembership(index)} sx={ACTION_ICON_SX}>
                <Delete fontSize="small" />
              </Button>
            </Tooltip>
          </Box>
          <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
            <InputLabel>Company</InputLabel>
            <Select value={am.companyId} label="Company"
              onChange={(e) => updateAdditionalMembershipCompany(index, e.target.value as string)}>
              {companiesNotMemberOf.map(c => (
                <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>
              ))}
              {am.companyId && !companiesNotMemberOf.find(c => c._id === am.companyId) && (
                <MenuItem value={am.companyId}>{getCompanyName(am.companyId)}</MenuItem>
              )}
            </Select>
          </FormControl>
          {am.companyId && renderModuleRows(
            am.moduleAndRole,
            am.companyId,
            (mIdx, field, val) => updateAdditionalModule(index, mIdx, field, val),
            (mIdx) => removeModuleFromAdditional(index, mIdx),
            () => addModuleToAdditional(index),
            false,
            true,
          )}
        </CardContent>
      </Card>
    );
  };

  // ──────────────────────────────────────────
  //  Main render
  // ──────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? 'Edit User' : 'New User'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>

            {userLimitError && <Alert severity="error">{userLimitError}</Alert>}

            {/* ─── COMPANY MEMBERSHIPS (edit mode) ─── */}
            {isEditing && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Company Memberships</Typography>

                {membershipsLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {membershipError && (
                  <Alert severity="error" sx={{ mb: 1 }} onClose={() => setMembershipError('')}>
                    {membershipError}
                  </Alert>
                )}

                {!membershipsLoading && memberships.length > 0 && (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {memberships.map(renderMembershipCard)}
                  </Box>
                )}

                {!membershipsLoading && memberships.length === 0 && !membershipError && (
                  <Typography variant="body2" color="text.secondary">No company memberships found.</Typography>
                )}

                {addMembershipOpen ? renderAddMembershipForm() : (
                  <Button size="small" startIcon={<Add />} variant="outlined" color="secondary"
                    sx={{ mt: 1, alignSelf: 'flex-start' }}
                    disabled={companiesNotMemberOf.length === 0}
                    onClick={() => setAddMembershipOpen(true)}>
                    Add Company Membership
                  </Button>
                )}
              </Box>
            )}

            {/* ─── PRIMARY COMPANY + MODULES (new user mode) ─── */}
            {!isEditing && (
              <Box>
                <Typography variant="h6" sx={{ mb: 1 }}>Primary Company & Modules</Typography>

                {isUserSuperAdmin ? (
                  <FormControl fullWidth error={!!errors.companyId} sx={{ mb: 1.5 }}>
                    <InputLabel>Company *</InputLabel>
                    <Select value={formData.companyId || ''} label="Company *"
                      onChange={(e) => handleInputChange('companyId', e.target.value)}>
                      {companies.map(c => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                    </Select>
                    {errors.companyId && <FormHelperText>{errors.companyId}</FormHelperText>}
                  </FormControl>
                ) : (
                  <TextField label="Company" value={getCompanyName(formData.companyId || '')}
                    fullWidth InputProps={{ readOnly: true }} size="small" sx={{ mb: 1.5 }} />
                )}

                {errors.moduleAndRole && (
                  <Typography color="error" variant="body2" sx={{ mb: 1 }}>{errors.moduleAndRole}</Typography>
                )}

                {renderModuleRows(
                  formData.moduleAndRole || [],
                  formData.companyId || currentCompany?._id || '',
                  handleModuleRoleChange,
                  removeModuleRole,
                  addModuleRole,
                  false,
                  true,
                )}

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" sx={{ mb: 1 }}>Additional Company Memberships</Typography>

                {additionalMemberships.map(renderAdditionalMembership)}

                <Button size="small" startIcon={<Add />} variant="outlined" color="secondary"
                  sx={{ mt: 1, alignSelf: 'flex-start' }}
                  disabled={companiesNotMemberOf.length === 0}
                  onClick={addAdditionalMembership}>
                  Add Company Membership
                </Button>
              </Box>
            )}

            {/* ─── BASIC INFO ─── */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Basic Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="Email *" value={formData.email || ''}
                onChange={(e) => handleInputChange('email', e.target.value)}
                error={!!errors.email} helperText={errors.email} fullWidth />
              <TextField label="Mobile *" value={formData.mobile || ''}
                onChange={(e) => handleInputChange('mobile', e.target.value)}
                error={!!errors.mobile} helperText={errors.mobile} fullWidth />
              <FormControl fullWidth error={!!errors.status}>
                <InputLabel>Status *</InputLabel>
                <Select value={formData.status || 'ACTIVE'} label="Status *"
                  onChange={(e) => handleInputChange('status', e.target.value)}>
                  <MenuItem value="ACTIVE">Active</MenuItem>
                  <MenuItem value="INACTIVE">Inactive</MenuItem>
                  <MenuItem value="PENDING">Pending</MenuItem>
                </Select>
                {errors.status && <FormHelperText>{errors.status}</FormHelperText>}
              </FormControl>
            </Box>

            {/* ─── PROFILE ─── */}
            <Divider sx={{ my: 1 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>Profile Information</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField label="First Name *" value={formData.profile?.firstName || ''}
                onChange={(e) => handleInputChange('profile.firstName', e.target.value)}
                error={!!errors['profile.firstName']} helperText={errors['profile.firstName']} fullWidth />
              <TextField label="Last Name *" value={formData.profile?.lastName || ''}
                onChange={(e) => handleInputChange('profile.lastName', e.target.value)}
                error={!!errors['profile.lastName']} helperText={errors['profile.lastName']} fullWidth />
              <TextField label="Date of Birth *" type="date"
                value={formData.profile?.dob ? formatDateForInput(formData.profile.dob) : ''}
                onChange={(e) => handleInputChange('profile.dob', e.target.value ? new Date(e.target.value) : new Date())}
                error={!!errors['profile.dob']} helperText={errors['profile.dob']}
                fullWidth InputLabelProps={{ shrink: true }} />
              <TextField label="Country *" value={formData.profile?.country || ''}
                onChange={(e) => handleInputChange('profile.country', e.target.value)}
                error={!!errors['profile.country']} helperText={errors['profile.country']} fullWidth />
              <TextField label="Address Line 1" value={formData.profile?.addressLine1 || ''}
                onChange={(e) => handleInputChange('profile.addressLine1', e.target.value)} fullWidth />
              <TextField label="Address Line 2" value={formData.profile?.addressLine2 || ''}
                onChange={(e) => handleInputChange('profile.addressLine2', e.target.value)} fullWidth />
              <TextField label="City" value={formData.profile?.city || ''}
                onChange={(e) => handleInputChange('profile.city', e.target.value)} fullWidth />
              <TextField label="State" value={formData.profile?.state || ''}
                onChange={(e) => handleInputChange('profile.state', e.target.value)} fullWidth />
              <TextField label="ZIP Code" value={formData.profile?.zip || ''}
                onChange={(e) => handleInputChange('profile.zip', e.target.value)} fullWidth autoComplete="off" />
              {!isEditing && (
                <TextField label="Password *" type="password"
                  onChange={(e) => handleInputChange('password', e.target.value)}
                  error={!!errors.password} helperText={errors.password}
                  fullWidth autoComplete="new-password" />
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={onClose} startIcon={<Cancel />} disabled={saveLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} startIcon={<Save />} variant="contained" color="success"
            disabled={saveLoading || !isFormValid()}>
            {saveLoading ? 'Saving...' : (isEditing ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Error Dialog */}
      <Dialog open={errorDialogOpen} onClose={() => { setErrorDialogOpen(false); setErrorMessage(''); }} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Error</DialogTitle>
        <DialogContent>
          <Typography sx={{ pt: 1, whiteSpace: 'pre-wrap' }}>{errorMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setErrorDialogOpen(false); setErrorMessage(''); }} color="primary" variant="contained">OK</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
